import { useId, useState, type ReactNode } from 'react';
import { HelpCircle } from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * A small "?" affordance that explains a term in place — the delivery vehicle
 * for the product glossary (context, confidence, knowledge, …). Opens on hover
 * and focus for pointer users, on click/tap for touch; dismisses on blur,
 * mouse-out, Escape, or a second click.
 */
export function InfoTip({
  children,
  label = 'What is this?',
  className,
  side = 'top',
}: {
  /** The explanation. Keep it to two short sentences — mechanics, not marketing. */
  children: ReactNode;
  /** Accessible name for the trigger, e.g. "What is context?" */
  label?: string;
  className?: string;
  side?: 'top' | 'bottom';
}) {
  const id = useId();
  const [open, setOpen] = useState(false);

  return (
    <span
      className={cn('relative inline-flex', className)}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        aria-label={label}
        aria-describedby={open ? id : undefined}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false);
        }}
        className="inline-flex items-center text-fg-subtle transition hover:text-fg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded-full"
      >
        <HelpCircle className="h-3.5 w-3.5" aria-hidden />
      </button>
      {open && (
        <span
          id={id}
          role="tooltip"
          className={cn(
            'absolute left-1/2 z-50 w-64 -translate-x-1/2 rounded-lg border border-border bg-surface px-3 py-2 text-xs font-normal normal-case leading-relaxed text-fg-muted shadow-pop',
            side === 'top' ? 'bottom-full mb-1.5' : 'top-full mt-1.5',
          )}
        >
          {children}
        </span>
      )}
    </span>
  );
}
